import { useState } from 'react';

import DOMPurify from 'dompurify';

import { NotificationCellProps, formatTimestamp } from '@knocklabs/react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { getMarkdownBlock } from '@/pages/NotificationSettings/utils';
import theme from '@/theme';
import { DeepReadonly, Nullable } from '@/utils/globalTypes';

type NotificationData = {
  entity_link?: string;
  account_hashid?: string;
  campaign_hashid?: string;
};

export type FeedItem = NotificationCellProps['item'];

export type FeedItemAction = 'markAsRead' | 'markAsUnread' | 'archive';

type NotificationFeedCellProps = {
  item: FeedItem;
  onItemClick?: (entityUrl: Nullable<string>) => void;
  onItemActionClick?: (action: FeedItemAction) => void;
};

type FeedItemOption = {
  label: string;
  value: FeedItemAction;
  shouldRender?: boolean;
};

const NotificationFeedCell = ({
  item,
  onItemClick,
  onItemActionClick,
}: NotificationFeedCellProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isActionMenuOpen = !!anchorEl;
  const isRead = item.read_at != null;
  const bodyMessage = getMarkdownBlock(item.blocks)?.rendered ?? '';
  const insertedDate = formatTimestamp(item.inserted_at);

  const ACTION_MENU_ITEMS: DeepReadonly<FeedItemOption[]> = [
    {
      label: 'Mark as Read',
      value: 'markAsRead',
      shouldRender: !isRead,
    },
    {
      label: 'Mark as Unread',
      value: 'markAsUnread',
      shouldRender: isRead,
    },
    {
      label: 'Archive',
      value: 'archive',
    },
  ];

  const handleItemClick = () => {
    const data = item.data as NotificationData;

    // Use entity_link if available (extract pathname if it's a full URL)
    if (data?.entity_link) {
      try {
        const url = new URL(data.entity_link);
        onItemClick?.(url.pathname);
      } catch {
        // If not a valid URL, use as-is (relative path)
        onItemClick?.(data.entity_link);
      }
      return;
    }

    // TODO: Remove this fallback once all notification templates include entity_link
    const { account_hashid: clientId, campaign_hashid: campaignId } =
      data ?? {};
    if (clientId && campaignId) {
      onItemClick?.(`/${clientId}/campaigns/${campaignId}/schedule`);
      return;
    }

    onItemClick?.(null);
  };

  return (
    <Stack
      direction="row"
      alignItems="start"
      gap={2}
      sx={{
        px: 2,
        pb: 2,
        pt: 1.5,
        cursor: 'pointer',
        bgcolor: isRead ? 'transparent' : 'neutral00',
      }}
      onClick={handleItemClick}
      className="feed-cell"
      data-cy="notification-feed-cell"
    >
      <Stack direction="row" gap={1}>
        <Box
          sx={{
            minWidth: '8px',
            height: '8px',
            mt: 1.75,
            borderRadius: '50%',
            bgcolor: (theme) =>
              isRead ? theme.palette.grey[200] : theme.palette.primary.main,
          }}
          data-cy="notification-feed-cell-dot"
        />

        <Stack direction="column" gap={1} sx={{ width: '100%' }}>
          <Typography
            variant="body2"
            color={isRead ? 'text.secondary' : 'text.primary'}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bodyMessage),
            }}
            sx={{
              '& > p, & .notification__message': {
                margin: 0,
              },
              '& .notification__header': {
                ...theme.typography.body2,
                fontWeight: 600,
                my: 1,
              },
              '& .notification': {
                alignItems: 'start',
                display: 'flex',
                gap: 2,
              },
            }}
            data-cy="notification-feed-cell-text"
          />

          <Typography variant="caption" color="text.secondary" ml={6.5}>
            {insertedDate}
          </Typography>
        </Stack>
      </Stack>
      <IconButton
        id="actions-button"
        aria-controls={isActionMenuOpen ? 'notification-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isActionMenuOpen ? 'true' : undefined}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          ml: 'auto',
          // TODO: remove this once the menu actions are revisited
          display: 'none',
        }}
        data-cy="notification-feed-cell-actions"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={isActionMenuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'actions-button',
          },
        }}
      >
        {ACTION_MENU_ITEMS.filter(
          ({ shouldRender }) => shouldRender ?? true,
        ).map(({ label, value }) => (
          <MenuItem
            key={value}
            onClick={(e) => {
              e.stopPropagation();
              onItemActionClick?.(value);
              setAnchorEl(null);
            }}
            data-cy={`notification-feed-cell-action-${value}`}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

export default NotificationFeedCell;
